import React, { lazy, Suspense, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { AppProvider } from './context/AppContext';
import { initGlobalErrorHandlers } from './lib/errorManager';
import { registerStudioRedirectShellCleanup } from './lib/studioRedirectShell';
import { scheduleNonCriticalStartup } from './lib/deferNonCriticalStartup';
import './index.css';

initGlobalErrorHandlers();
registerStudioRedirectShellCleanup();

const ToastContainer = lazy(async () => {
  await import('react-toastify/dist/ReactToastify.css');
  const mod = await import('react-toastify');
  return { default: mod.ToastContainer };
});

function DeferredToastContainer() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let done = false;
    const enable = () => {
      if (done) return;
      done = true;
      cleanup();
      setReady(true);
    };
    const onInput = () => enable();
    const events = ['pointerdown', 'keydown', 'touchstart'] as const;
    for (const type of events) {
      window.addEventListener(type, onInput, { once: true, passive: true });
    }
    const fallback = window.setTimeout(enable, 10000);
    const cleanup = () => {
      window.clearTimeout(fallback);
      for (const type of events) window.removeEventListener(type, onInput);
    };
    return cleanup;
  }, []);

  if (!ready) return null;
  return (
    <Suspense fallback={null}>
      <ToastContainer position="top-right" autoClose={4000} newestOnTop pauseOnFocusLoss={false} />
    </Suspense>
  );
}

const rootElement = document.getElementById('root')!;

const app = (
  <React.StrictMode>
    <BrowserRouter>
      <AppProvider>
        <App />
        <DeferredToastContainer />
      </AppProvider>
    </BrowserRouter>
  </React.StrictMode>
);

createRoot(rootElement).render(app);
scheduleNonCriticalStartup();
